import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Cinematic auto-crossfading hero slideshow with Ken Burns effect.
 * Each slide slowly zooms/pans while cross-dissolving into the next.
 */
const SLIDES = [
  {
    src: "https://images.unsplash.com/photo-1611821064430-0d40291d0f0b?crop=entropy&cs=srgb&fm=jpg&fit=crop&w=1400&h=1750&q=85",
    alt: "Black BMW convertible — auto insurance",
    label: "Auto",
    kbFrom: { scale: 1.08, x: 0, y: 0 },
    kbTo: { scale: 1.14, x: -8, y: -4 },
  },
  {
    src: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&fit=crop&w=1400&h=1750&q=85",
    alt: "Modern residential home — property insurance",
    label: "Property",
    kbFrom: { scale: 1.10, x: 0, y: 0 },
    kbTo: { scale: 1.18, x: 6, y: 6 },
  },
  {
    src: "https://images.unsplash.com/photo-1517090504586-fde19ea6066f?crop=entropy&cs=srgb&fm=jpg&fit=crop&w=1400&h=1750&q=85",
    alt: "Toronto skyline — commercial insurance",
    label: "Commercial",
    kbFrom: { scale: 1.10, x: 0, y: 0 },
    kbTo: { scale: 1.16, x: -6, y: 4 },
  },
];

const INTERVAL_MS = 4500;

export default function HeroSlideshow() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  const active = SLIDES[index];

  return (
    <div
      data-testid="hero-slideshow"
      className="relative aspect-[4/5] overflow-hidden border border-bone-100/15 bg-forest-950"
    >
      <AnimatePresence mode="popLayout">
        <motion.div
          key={index}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.6, ease: [0.19, 1, 0.22, 1] }}
        >
          <motion.img
            src={active.src}
            alt={active.alt}
            data-testid="hero-image"
            className="w-full h-full object-cover select-none pointer-events-none"
            initial={active.kbFrom}
            animate={active.kbTo}
            transition={{ duration: INTERVAL_MS / 1000 + 1.6, ease: "linear" }}
            draggable={false}
          />
          {/* subtle bottom-to-top gradient for legibility of overlays */}
          <div className="absolute inset-0 bg-gradient-to-t from-forest-900/40 via-transparent to-transparent" />
          {/* rotating category label — bottom-left */}
          <motion.div
            key={`label-${index}`}
            className="absolute left-4 bottom-4 flex items-center gap-2.5 bg-forest-900/80 backdrop-blur border border-bone-100/15 px-3 py-2"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-terracotta-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-terracotta-500"></span>
            </span>
            <span className="overline text-terracotta-400">Now insuring · {active.label}</span>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* slide indicators — top-right */}
      <div className="absolute top-4 right-4 flex items-center gap-1.5">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            aria-label={`Show slide ${i + 1}`}
            data-testid={`hero-slide-dot-${i}`}
            className={`h-[3px] transition-all ${
              i === index ? "w-8 bg-terracotta-500" : "w-4 bg-bone-100/40 hover:bg-bone-100/70"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
